import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink, Loader2, FileText } from "lucide-react";
import { fetchAttachmentUrl, viewableKind, downloadAttachment, openDocumentPreview } from "@/lib/files";
import { toast } from "sonner";

/* In-app document viewer — renders PDFs/images/text inline (iframe/img) so documents can be VIEWED
   without downloading. Download and "Open in new tab" remain available as secondary actions. */
export function DocumentViewer({ attachment, open, onClose }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const name = attachment?.original_filename || attachment?.filename || "Document";
  const kind = attachment ? viewableKind(name, attachment.mime_type) : "other";

  useEffect(() => {
    if (!open || !attachment) return;
    let obj;
    setLoading(true); setErr(false); setUrl(null);
    fetchAttachmentUrl(`/attachments/${attachment.id}/file`, name)
      .then((u) => { obj = u; setUrl(u); })
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
    return () => { if (obj) setTimeout(() => window.URL.revokeObjectURL(obj), 1000); };
  }, [open, attachment]);

  const dl = async () => {
    try { await downloadAttachment(`/attachments/${attachment.id}/file`, name); }
    catch { toast.error("Couldn't download the document."); }
  };
  const newTab = () => {
    if (kind === "pdf" || kind === "docx") openDocumentPreview(`/attachments/${attachment.id}/file`, name);
    else if (url) window.open(url, "_blank", "noopener");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="bg-white max-w-5xl w-[92vw] h-[88vh] flex flex-col p-0 gap-0 overflow-hidden" data-testid="document-viewer">
        <DialogHeader className="px-4 py-3 border-b border-slate-200">
          <DialogTitle className="flex items-center gap-2 text-base min-w-0">
            <FileText className="w-4 h-4 text-brand shrink-0" /><span className="truncate">{name}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 bg-slate-100">
          {loading ? (
            <div className="h-full flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : err ? (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-slate-500">
              <p className="text-sm">Couldn't load a preview for this file.</p>
              <Button variant="outline" onClick={dl} data-testid="document-viewer-download-fallback"><Download className="w-4 h-4 mr-1.5" /> Download instead</Button>
            </div>
          ) : kind === "pdf" || kind === "text" ? (
            <iframe title={name} src={url} className="w-full h-full border-0 bg-white" data-testid="document-viewer-frame" />
          ) : kind === "image" ? (
            <div className="h-full overflow-auto flex items-center justify-center p-4">
              <img src={url} alt={name} className="max-w-full max-h-full object-contain" data-testid="document-viewer-image" />
            </div>
          ) : kind === "docx" ? (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-slate-500 px-6 text-center">
              <FileText className="w-10 h-10 text-slate-300" />
              <p className="text-sm">Word documents open in the Top-Recruit document viewer.</p>
              <Button onClick={newTab} className="bg-brand hover:bg-brand-strong" data-testid="document-viewer-docx-open"><ExternalLink className="w-4 h-4 mr-1.5" /> Open preview in new tab</Button>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-slate-500 px-6 text-center">
              <FileText className="w-10 h-10 text-slate-300" />
              <p className="text-sm">This file type can't be previewed in the browser.</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={newTab} data-testid="document-viewer-newtab"><ExternalLink className="w-4 h-4 mr-1.5" /> Open in new tab</Button>
                <Button onClick={dl} className="bg-brand hover:bg-brand-strong" data-testid="document-viewer-download"><Download className="w-4 h-4 mr-1.5" /> Download</Button>
              </div>
            </div>
          )}
        </div>
        <div className="px-4 py-2.5 border-t border-slate-200 flex items-center justify-end gap-2">
          {url && <Button size="sm" variant="ghost" onClick={newTab} data-testid="document-viewer-open-newtab"><ExternalLink className="w-4 h-4 mr-1.5" /> Open in new tab</Button>}
          <Button size="sm" variant="outline" onClick={dl} data-testid="document-viewer-download-btn"><Download className="w-4 h-4 mr-1.5" /> Download</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
